import { AutoComplete } from "primereact/autocomplete";
import { Chip } from "primereact/chip";
import { Dialog } from "primereact/dialog";
import React, { useCallback, useState } from "react";
import { getCityByCoordinates, searchCities } from "../../services/cityService";
import debounce from "lodash.debounce";
import { useAuth } from "../../contexts/AuthContext";
import { useMessageLogic } from "../../hooks/useMessageLogic";
import { useToast } from "../../contexts/ToastContext";

type LocationSelectorProps = {
  autoSelect?: boolean; // Optional property
};

const LocationSelector: React.FC<LocationSelectorProps> = ({
  autoSelect = false,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  const { selectedLocation, setLocation, selectedLocationZip, setLocationZip } =
    useAuth();

  const { showSuccessToast, showErrorToast } = useToast();

  const [visible, setVisible] = useState(false);

  const [value, setValue] = useState("");

  const [items, setItems] = useState<string[]>([]);

  const [detectingLocation, setDetectingLocation] = useState(false);

  const search = useCallback(
    debounce(async (event: { query: string }) => {
      try {
        const response = await searchCities(event.query.toLowerCase());
        const location = response.data.data; // Extract data from AxiosResponse
        const cityNames = location.map(
          (loc: { city: string; zip: string; state_name: string }) =>
            // loc.city + " (Zip: " + loc.zip + ")"
            loc.city + ", " + loc.state_name + " (Zip: " + loc.zip + ")"
        );
        setItems(cityNames || []);
      } catch (err) {
        setItems([]);
      }
    }, 300), // 300ms debounce delay
    []
  );

  const popularCities = [
    { name: "New York", zip: "10001" },
    { name: "Los Angeles", zip: "90001" },
    { name: "Chicago", zip: "60601" },
    // { name: "Houston", zip: "77001" },
    // { name: "Boston", zip: "02101" },
    { name: "Washington", zip: "20001" },
    { name: "San Francisco", zip: "94101" },
    { name: "Seattle", zip: "98101" },
    // { name: "Miami", zip: "33101" },
    // { name: "Denver", zip: "80201" },
  ];

  // open the dialog if selectedLocation is null
  React.useEffect(() => {
    if (!selectedLocation && autoSelect) {
      setVisible(true);
    }
  }, [selectedLocation]);

  // on change of the input, we will search for the cities
  const handleChange = (e: string) => {
    const cityMatch = e.match(/^(.*) \(Zip: (\d+)\)$/);
    if (cityMatch) {
      const city = cityMatch[1].split(",")[0].trim();
      const zip = cityMatch[2].trim();
      setLocation(city);
      setLocationZip(zip);
      showSuccessToast(`Location set to ${city} (Zip: ${zip})`);
      reloadPage();
    } else {
      showErrorToast("Invalid location format. Please try again.");
    }
    setValue("");
    setVisible(false);
  };

  // Detect location using Geolocation API
  const detectLocation = async () => {
    if (!navigator.geolocation) {
      showErrorToast("Geolocation is not supported by your browser.");
      return;
    }

    setDetectingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await getCityByCoordinates(
            latitude.toString(),
            longitude.toString()
          );
          const city = response.data.data.city;
          const zip = response.data.data.zip;
          if (!city) {
            showErrorToast(
              "Unable to detect your city. Please enter manually."
            );
            setDetectingLocation(false);
            return;
          }
          setLocation(city);
          setLocationZip(zip);
          showSuccessToast(`Location set to ${city} (Zip: ${zip})`);
          reloadPage();
        } catch (err) {
          showErrorToast("Unable to detect your city. Please enter manually.");
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        showErrorToast(
          "Unable to retrieve your location. Please check your permissions."
        );
        setDetectingLocation(false);
      }
    );
  };

  const reloadPage = () => {
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <>
      <Chip
        className="cursor-pointer"
        label={selectedLocation ? selectedLocation : "Select Location"}
        icon="pi pi-map-marker"
        onClick={() => setVisible(true)}
      />
      <Dialog
        visible={visible}
        showHeader={true}
        header="Select Your Location"
        position="center"
        style={{ width: "60vw" }}
        onHide={() => {
          if (!visible) return;
          setVisible(false);
        }}
        draggable={false}
        resizable={false}
      >
        <div className="col-12 d-flex flex-row">
          <AutoComplete
            className="w-100"
            inputId="ac"
            value={value}
            suggestions={items}
            completeMethod={search}
            dropdownMode="current"
            onChange={(e) => setValue(e.value)}
            onSelect={(e) => handleChange(e.value)}
            emptyMessage="No cities found"
            placeholder="Search for your city or state or zip code"
          />
        </div>
        <div className="col-12 d-flex flex-row">
          <a
            onClick={(e) => {
              if (detectingLocation) {
                e.preventDefault();
                return;
              }
              detectLocation();
            }}
            style={{
              cursor: detectingLocation ? "not-allowed" : "pointer",
              pointerEvents: detectingLocation ? "none" : "auto",
              opacity: detectingLocation ? 0.5 : 1,
            }}
            className="ms-3"
          >
            {detectingLocation ? "Detecting..." : "Detect My Location"}
          </a>
        </div>
        <hr />
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          Popular Cities
        </div>
        <div className="container text-center">
          <div className="row">
            {popularCities.map((city, index) => (
              <div className="col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                <a
                  className={index > 0 ? "" : ""}
                  onClick={() =>
                    handleChange(city.name + " (Zip: " + city.zip + ")")
                  }
                  style={{ cursor: "pointer", textDecoration: "none" }}
                >
                  <div className="iconCategoryCard">
                    <div className="iconCategoryCardImageWrapper">
                      <img
                        height={50}
                        src={
                          baseUrl +
                          "/assets/images/cities/" +
                          city.name +
                          ".png"
                        }
                        alt=""
                      />
                    </div>
                    <span>{city.name}</span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default LocationSelector;
